import { Link } from 'react-router';

/**
 * Pagina pubblica Norme sulla privacy (route /privacy, fuori da DashboardLayout).
 * Serve come URL ufficiale richiesto da Google Play Console (campo "Norme sulla
 * privacy") ed e' accessibile senza login. I contenuti riflettono il
 * trattamento dati reale dell'app: account, posizione GPS per selettive e
 * ricerca persona, notifiche push via FCM, hosting Hetzner (UE). Nessuna
 * pubblicita', nessuna vendita di dati a terzi.
 *
 * NB: documento da far validare da un legale/DPO prima del go-live pubblico.
 */

const LAST_UPDATED = '30 giugno 2026';
const PRIVACY_EMAIL = 'software@gsolutions-it.com';

const sectionStyle: React.CSSProperties = { marginBottom: 24 };
const h2Style: React.CSSProperties = {
  fontSize: 18, fontWeight: 700, color: '#b71c1c', margin: '0 0 8px',
};
const pStyle: React.CSSProperties = { margin: '0 0 10px', lineHeight: 1.6, color: '#333' };
const liStyle: React.CSSProperties = { marginBottom: 6, lineHeight: 1.6, color: '#333' };

export function PrivacyPolicyPage(): React.JSX.Element {
  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: '24px 16px' }}>
      <div style={{
        maxWidth: 800, margin: '0 auto', background: '#fff', borderRadius: 12,
        padding: '32px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      }}>
        <header style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/icon-128.webp" alt="VVF Tracker"
            style={{ width: 64, height: 64, margin: '0 auto 8px', display: 'block' }} />
          <h1 style={{ fontSize: 24, margin: '0 0 4px', color: '#222' }}>
            Informativa sulla Privacy
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
            VVF Tracker — Gestionale per Vigili del Fuoco volontari
          </p>
          <p style={{ margin: '4px 0 0', color: '#999', fontSize: 13 }}>
            Ultimo aggiornamento: {LAST_UPDATED}
          </p>
        </header>

        <section style={sectionStyle}>
          <p style={pStyle}>
            La presente informativa descrive come l'applicazione <strong>VVF Tracker</strong>{' '}
            ("l'App") tratta i dati personali degli utenti, in conformità al Regolamento (UE)
            2016/679 (GDPR). L'App è uno strumento gestionale a uso interno dei distaccamenti
            dei Vigili del Fuoco volontari, riservato al personale autorizzato.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>1. Titolare del trattamento e contatti</h2>
          <p style={pStyle}>
            Ciascun distaccamento o ente che utilizza l'App è titolare del trattamento dei dati
            inseriti dai propri utenti. Lo sviluppo e la gestione tecnica dell'infrastruttura sono
            affidati a <strong>G soLutions di Luzu Gabriele</strong> (Via Cherubini 3, 41043
            Formigine MO — P.IVA 04227230366), in qualità di responsabile del trattamento.
          </p>
          <p style={pStyle}>
            Per qualsiasi richiesta relativa ai dati personali è possibile scrivere a:{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} style={{ color: '#1565c0' }}>{PRIVACY_EMAIL}</a>.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>2. Dati che raccogliamo</h2>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li style={liStyle}>
              <strong>Dati dell'account:</strong> nome, soprannome, indirizzo email,
              numero di telefono, grado e qualifiche/competenze, ruolo e permessi.
            </li>
            <li style={liStyle}>
              <strong>Dati di attività:</strong> interventi registrati, disponibilità,
              risposte alle chiamate selettive, dati di mezzi e magazzino, scadenze e
              dati di addestramento.
            </li>
            <li style={liStyle}>
              <strong>Posizione geografica (GPS):</strong> raccolta solo quando l'utente
              risponde a una chiamata selettiva o partecipa a una ricerca persona, per
              coordinare l'intervento (anche in background durante l'operazione).
            </li>
            <li style={liStyle}>
              <strong>Contenuti caricati:</strong> documenti e foto (es. schede idranti,
              documenti di servizio) inseriti dagli utenti.
            </li>
            <li style={liStyle}>
              <strong>Dati tecnici:</strong> token per le notifiche push e informazioni
              di base sul dispositivo necessarie al funzionamento.
            </li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>3. Finalità e basi giuridiche</h2>
          <p style={pStyle}>
            I dati sono trattati esclusivamente per la gestione operativa del distaccamento:
            organizzazione degli interventi, coordinamento delle chiamate selettive e delle
            ricerche persona, gestione di mezzi, magazzino, scadenze e personale. La base
            giuridica è l'esecuzione di compiti di interesse pubblico e il legittimo interesse
            del distaccamento; il trattamento della posizione in tempo reale avviene sulla base
            del consenso dell'utente, revocabile in qualsiasi momento dalle impostazioni del
            dispositivo.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>4. Posizione in background</h2>
          <p style={pStyle}>
            L'App può accedere alla posizione anche quando è in background, ma <strong>solo
            durante una chiamata selettiva attiva o una ricerca persona</strong> a cui l'utente
            ha scelto di partecipare. Serve a mostrare ai colleghi la posizione e a stimare i
            tempi di arrivo. Fuori da queste operazioni la posizione non viene raccolta.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>5. Notifiche push</h2>
          <p style={pStyle}>
            Per inviare gli avvisi (es. nuove chiamate selettive) l'App utilizza{' '}
            <strong>Firebase Cloud Messaging</strong> di Google. A tale scopo viene generato e
            conservato un token identificativo del dispositivo. Le notifiche sono essenziali al
            funzionamento del servizio.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>6. Dove e per quanto conserviamo i dati</h2>
          <p style={pStyle}>
            I dati sono conservati su server situati nell'Unione Europea (Hetzner Online GmbH,
            Germania). I dati vengono conservati per il tempo necessario alle finalità descritte
            e, comunque, nel rispetto degli obblighi di legge applicabili. Al termine del
            trattamento i dati personali vengono cancellati o resi anonimi.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>7. Condivisione dei dati</h2>
          <p style={pStyle}>
            I dati non vengono venduti né ceduti a terzi per finalità commerciali. Sono
            accessibili unicamente al personale autorizzato del distaccamento e ai fornitori
            tecnici che agiscono come responsabili del trattamento (hosting Hetzner; Google per
            il solo servizio di notifiche push). <strong>L'App non contiene pubblicità e non
            traccia gli utenti per finalità pubblicitarie.</strong>
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>8. Sicurezza</h2>
          <p style={pStyle}>
            Adottiamo misure tecniche e organizzative adeguate: cifratura in transito (TLS),
            accesso ai sistemi basato su ruoli, isolamento dei dati per distaccamento, backup
            periodici e registrazione degli accessi amministrativi.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>9. Diritti dell'interessato</h2>
          <p style={pStyle}>
            In qualsiasi momento puoi esercitare i diritti previsti dagli artt. 15-22 GDPR:
            accesso, rettifica, cancellazione, limitazione, opposizione e portabilità dei dati.
            Per farlo scrivi a{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} style={{ color: '#1565c0' }}>{PRIVACY_EMAIL}</a>.
            Hai inoltre il diritto di proporre reclamo all'Autorità Garante per la protezione
            dei dati personali (www.garanteprivacy.it).
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>10. Minori</h2>
          <p style={pStyle}>
            L'App è riservata al personale del distaccamento e non è destinata né indirizzata
            a minori di 16 anni.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>11. Modifiche all'informativa</h2>
          <p style={pStyle}>
            Eventuali aggiornamenti saranno pubblicati in questa pagina con la relativa data di
            revisione. Ti invitiamo a consultarla periodicamente.
          </p>
        </section>

        <footer style={{ borderTop: '1px solid #eee', paddingTop: 16, marginTop: 8, textAlign: 'center' }}>
          <Link to="/" style={{ color: '#1565c0', fontSize: 14 }}>← Torna alla home</Link>
        </footer>
      </div>
    </div>
  );
}
